/**
 * Config-driven form modal used for creating/editing a single row.
 *
 * Each field is described by an object:
 *   { name, label, type, required?, placeholder?, maxLen?,
 *     options? (for select),       // [{value, label}]
 *     lookupOptions?,               // pre-resolved [{value, label}] for FK selects
 *     help?, rows? (textarea height) }
 *
 * type: 'text' | 'textarea' | 'select' | 'lookup' | 'number' | 'date' | 'checkbox'
 *
 * Mode: pass `initial` to switch to edit mode. The modal does shallow diff
 * via partial PATCH semantics — only changed fields are sent.
 */

import React, { useEffect, useRef, useState } from 'react';
import Modal from '../Modal';
import { SearchableSelect } from './filters/FilterDropdowns';

function emptyValueForField(field) {
  switch (field.type) {
    case 'checkbox': return false;
    case 'number':   return '';
    default:         return '';
  }
}

function buildInitialValues(fields, initial) {
  const v = {};
  for (const f of fields) {
    if (initial && f.name in initial && initial[f.name] != null) {
      v[f.name] = f.type === 'checkbox' ? !!initial[f.name] : String(initial[f.name]);
    } else {
      v[f.name] = emptyValueForField(f);
    }
  }
  return v;
}

function normaliseOnSubmit(fields, values) {
  const out = {};
  for (const f of fields) {
    let v = values[f.name];
    if (f.type === 'checkbox') {
      out[f.name] = !!v;
      continue;
    }
    if (typeof v === 'string') v = v.trim();
    if (v === '' || v == null) {
      out[f.name] = null;
      continue;
    }
    if (f.type === 'number') {
      const n = Number(v);
      out[f.name] = Number.isFinite(n) ? n : null;
    } else if (f.type === 'lookup' || f.type === 'select') {
      // Lookup options use string values; coerce to number when numeric.
      const n = Number(v);
      out[f.name] = Number.isFinite(n) && String(n) === v ? n : v;
    } else {
      out[f.name] = v;
    }
  }
  return out;
}

export default function EntityFormModal({
  open,
  title,
  fields,
  initial,           // null → create mode; an object → edit mode
  onSubmit,          // (body) => Promise
  onClose,
  submitLabel,       // optional override
}) {
  const isEdit = !!initial;
  const [values, setValues] = useState(() => buildInitialValues(fields, initial));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // `fields` is defined inline in calling components — new reference every render.
  // Use a ref so the reset effect doesn't fire on every parent re-render (e.g. when
  // workspace context refreshes silently in the background).
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  useEffect(() => {
    if (open) {
      setValues(buildInitialValues(fieldsRef.current, initial));
      setError(null);
      setSubmitting(false);
    }
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    // Required-on-create check (client-side only; backend re-validates).
    for (const f of fields) {
      if (f.required && !isEdit) {
        const v = values[f.name];
        if (v == null || (typeof v === 'string' && v.trim() === '')) {
          setError(`${f.label} is required`);
          return;
        }
      }
    }
    const body = normaliseOnSubmit(fields, values);
    setSubmitting(true);
    try {
      await onSubmit?.(body);
      onClose?.();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Save failed',
      );
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title={title} onClose={submitting ? undefined : onClose}>
      <form onSubmit={handleSubmit} className="entity-form">
        {fields.map((f) => (
          <FieldRow
            key={f.name}
            field={f}
            value={values[f.name]}
            onChange={(v) => setField(f.name, v)}
            half={!!f.half}
          />
        ))}
        {error && <div className="form-error">{error}</div>}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel || (isEdit ? 'Save Changes' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FieldRow({ field, value, onChange, half }) {
  const id = `f-${field.name}`;
  const common = {
    id,
    className: 'form-input',
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    placeholder: field.placeholder,
  };

  let control;
  switch (field.type) {
    case 'textarea':
      control = (
        <textarea
          {...common}
          rows={field.rows || 4}
          maxLength={field.maxLen}
        />
      );
      break;
    case 'select':
      control = (
        <SearchableSelect
          value={value ?? ''}
          onChange={(v) => onChange(v)}
          options={[{ value: '', label: '— Choose —' }, ...(field.options || [])]}
          showAll={false}
          btnClass="form-select-btn"
          matchTriggerWidth
        />
      );
      break;
    case 'lookup':
      control = (
        <SearchableSelect
          value={value ?? ''}
          onChange={(v) => onChange(v || null)}
          options={[{ value: '', label: '— None —' }, ...(field.lookupOptions || [])]}
          showAll={false}
          btnClass="form-select-btn"
          matchTriggerWidth
        />
      );
      break;
    case 'number':
      control = (
        <input
          {...common}
          type="number"
          min={field.min}
          max={field.max}
          onKeyDown={(e) => {
            if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (!/^\d+$/.test(text)) e.preventDefault();
          }}
          onChange={(e) => {
            let v = e.target.value.replace(/[^0-9]/g, '');
            if (v !== '' && field.max != null && Number(v) > field.max) v = String(field.max);
            if (v !== '' && field.min != null && Number(v) < field.min) v = String(field.min);
            common.onChange({ target: { value: v } });
          }}
        />
      );
      break;
    case 'date':
      control = <input {...common} type="date" />;
      break;
    case 'checkbox':
      control = (
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          style={{ width: 16, height: 16, accentColor: '#1a472a' }}
        />
      );
      break;
    case 'text':
    default:
      control = (
        <input
          {...common}
          type="text"
          maxLength={field.maxLen}
          {...(field.alphanumeric ? {
            onChange: (e) => {
              const v = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
              onChange(v);
            },
            onPaste: (e) => {
              const text = e.clipboardData.getData('text');
              if (/[^a-zA-Z0-9 ]/.test(text)) e.preventDefault();
            },
          } : {})}
        />
      );
      break;
  }

  return (
    <label className={`form-label${half ? ' half' : ''}`} htmlFor={id}>
      <span>
        {field.label}
        {field.required && <span style={{ color: 'var(--status-danger-fg)' }}> *</span>}
      </span>
      {control}
      {field.help && <small className="form-help">{field.help}</small>}
    </label>
  );
}
